import { useState } from 'react';
import { momentService } from '../../../services/momentService';
import { attachmentService } from '../../../services/attachmentService';

const byDate = (a, b) => new Date(b.occurred_at) - new Date(a.occurred_at);

// Shared create / edit / delete logic for Moments, used by the main page and Connection profiles.
export const useMomentEditor = ({ user, setMoments }) => {
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');
  const [progress, setProgress] = useState('');
  const [formState, setFormState] = useState(null); // { moment } | null
  const [viewing, setViewing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const refresh = async (momentId) => {
    const fresh = await momentService.getMoment(momentId);
    setMoments((prev) => prev.map((m) => (m.id === fresh.id ? fresh : m)).sort(byDate));
  };

  // Links the chosen people to a saved Moment. A failure here never undoes the Moment.
  const syncPeople = async (momentId, contactIds = []) => {
    setWarning('');
    try {
      await momentService.setPeople(momentId, user.id, contactIds);
      await refresh(momentId);
    } catch (err) {
      setWarning('Your moment was saved, but the people could not be updated. Open it and try again.');
    }
  };

  // Removes and uploads attachments for a saved Moment. Returns how many operations failed.
  const syncAttachments = async (momentId, existing, values) => {
    const items = values.newItems || [];
    const removeIds = values.removeIds || [];
    if (!items.length && !removeIds.length) return 0;
    let failed = 0;
    for (const id of removeIds) {
      const att = existing.find((a) => a.id === id);
      if (!att) continue;
      try { await attachmentService.remove(att); } catch (err) { failed += 1; }
    }
    for (let i = 0; i < items.length; i += 1) {
      setProgress(`Uploading ${i + 1} of ${items.length}…`);
      try { await attachmentService.upload(user.id, momentId, items[i]); } catch (err) { failed += 1; }
    }
    setProgress('');
    try {
      await refresh(momentId);
    } catch (err) {
      failed += 0;
    }
    if (failed) {
      setWarning(`${failed} attachment change${failed > 1 ? 's' : ''} could not be completed. Open the moment to check, then try again.`);
    }
    return failed;
  };

  const handleSave = async (values) => {
    if (formState.moment) {
      const updated = await momentService.updateMoment(formState.moment.id, values);
      setMoments((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)).sort(byDate));
      await syncPeople(updated.id, values.contactIds);
      await syncAttachments(updated.id, formState.moment.attachments || [], values);
      flash('Moment updated.');
    } else {
      const created = await momentService.createMoment(user.id, values);
      setMoments((prev) => [created, ...prev].sort(byDate));
      await syncPeople(created.id, values.contactIds);
      const failed = await syncAttachments(created.id, [], values);
      const total = (values.newItems || []).length;
      if (!created.body && total > 0 && failed >= total) {
        // Nothing to keep: no note and no file made it. Remove the empty Moment.
        await momentService.deleteMoment(created.id);
        setMoments((prev) => prev.filter((m) => m.id !== created.id));
        setWarning('');
        throw new Error('Your files could not be uploaded, so nothing was saved. Please try again.');
      }
      flash('Moment saved.');
    }
    setFormState(null);
  };

  const handleDelete = async () => {
    await momentService.trashMoment(toDelete.id);
    setMoments((prev) => prev.filter((m) => m.id !== toDelete.id));
    setToDelete(null);
    setViewing(null);
    flash('Moved to Trash. You can restore it for 30 days.');
  };

  return {
    success, warning, progress,
    formState, setFormState,
    viewing, setViewing,
    toDelete, setToDelete,
    handleSave, handleDelete
  };
};